import { h, Fragment } from 'preact';
import { useRef, useMemo } from 'preact/hooks';
import { memo } from 'preact/compat';
import getRoute from '../utils/getRoute';
import { getConfigForCity } from '../city-config';

import VehicleChip from './VehicleChip';

function rowSpaner(stopGrid, column, rowIndex) {
  let span = 1;
  while (stopGrid[++rowIndex] && !stopGrid[rowIndex][column]) {
    stopGrid[rowIndex][column] = '___';
    span++;
  }
  return span;
}

const oppositeStopsCache = new WeakMap();

function getOppositeStops(stopsData) {
  if (oppositeStopsCache.has(stopsData)) return oppositeStopsCache.get(stopsData);

  const byParent = new Map();
  Object.values(stopsData).forEach((stop) => {
    if (!stop.parentStopID) return;
    const siblings = byParent.get(stop.parentStopID) || [];
    siblings.push(stop.number);
    byParent.set(stop.parentStopID, siblings);
  });

  const opposites = new Map();
  byParent.forEach((siblings) => {
    if (siblings.length < 2) return;
    siblings.forEach((stop) => {
      opposites.set(stop, siblings.find((candidate) => candidate !== stop));
    });
  });
  oppositeStopsCache.set(stopsData, opposites);
  return opposites;
}

const isOpposite = (stop, oppositeStops) => oppositeStops.has(stop);
const getOpposite = (stop, oppositeStops) => oppositeStops.get(stop) || null;

function areOpposite(stop1, stop2, stopsData) {
  if (!stop1 || !stop2 || !stopsData) return false;
  if (stop1 === stop2) return false;
  const stop1Info = stopsData[stop1];
  const stop2Info = stopsData[stop2];
  if (!stop1Info || !stop2Info) return false;
  return stop1Info.parentStopID && 
         stop1Info.parentStopID === stop2Info.parentStopID;
}

function StopsList(props) {
  const route = getRoute();

  const { routes, stopsData, cityCode, vehicles = [], onVehicleClick, stopTimeLabels } = props;
  
  if (
    !routes ||
    !routes.length ||
    !stopsData
  ) {
    console.warn('Invalid arguments');
    return null;
  }
  const oppositeStops = getOppositeStops(stopsData);

  // If there are more than 2 routes, select the 2 routes with the most stops
  let selectedRoutes = routes;
  if (routes.length > 2) {
    // Sort routes by length (number of stops) in descending order
    const sortedRoutes = [...routes].sort((a, b) => b.length - a.length);
    // Take the top 2 routes with the most stops
    selectedRoutes = sortedRoutes.slice(0, 2);
  }

  // Validate selected routes - ensure at least one route has more than 1 stop
  if (!selectedRoutes.length || !selectedRoutes.some(route => route && route.length > 1)) {
    console.warn('No valid routes with more than 1 stop');
    return null;
  }

  const [route1, route2] = selectedRoutes;

  // Ensure route1 exists before proceeding
  if (!route1) {
    console.warn('No routes available');
    return null;
  }

  const route1Len = route1.length;

  if (route1 && route2) {
    if (route1[0] === route2[0]) {
      console.warn('Both routes start from same point', route1[0]);
    }
    if (route1[route1.length - 1] === route2[route2.length - 1]) {
      console.warn('Both routes end at same point', route1[route1.length - 1]);
    }
    if (
      route1[route1.length - 1] === route2[0] &&
      route2[route2.length - 1] !== route1[0]
    ) {
      console.warn(
        'First route connects to the 2nd route. Why?',
        route1[route1.length - 1],
        route2[0],
        route2[route2.length - 1],
        route1[0],
      );
    }
  }
  const route1FirstStop = route1[0];
  const route1LastStop = route1[route1Len - 1];

  const lastStop = useRef();

  // Map vehicles to their positions based on API data
  const vehiclePositions = useMemo(() => {
    if (!vehicles || vehicles.length === 0) {
      return new Map();
    }
    
    const positions = new Map();
    
    vehicles.forEach(vehicle => {
      // Use the stop information from the API response
      // Vehicle is between "last" stop and "current/next" stop
      // We'll display the vehicle after the stop it has passed (lastLocationId)
      let stopKey = null;
      
      if (vehicle.stops) {
        // Priority: lastLocationId > currentLocationId > nextLocationId
        // This determines where we show the vehicle in the route list
        if (vehicle.stops.lastLocationId) {
          stopKey = String(vehicle.stops.lastLocationId);
        } else if (vehicle.stops.currentLocationId) {
          stopKey = String(vehicle.stops.currentLocationId);
        } else if (vehicle.stops.nextLocationId) {
          // Vehicles render after their key stop, so to show one before the
          // next stop it goes after the stop preceding it on the route.
          stopKey = String(vehicle.stops.nextLocationId);
          for (const route of [route1, route2]) {
            const nextIndex = route ? route.indexOf(stopKey) : -1;
            if (nextIndex > 0) {
              stopKey = String(route[nextIndex - 1]);
              break;
            }
          }
        }
      } else {
        console.warn(`Vehicle ${vehicle.vehicleNumber} has no stops information`, vehicle);
      }
      
      if (stopKey) {
        if (!positions.has(stopKey)) {
          positions.set(stopKey, []);
        }
        positions.get(stopKey).push(vehicle);
      } else {
        console.warn(`Could not determine position for vehicle ${vehicle.vehicleNumber}`, vehicle);
      }
    });
    
    return positions;
  }, [vehicles, route1, route2]);

  const StopLink = ({ stop }) => {
    if (!stop || !stopsData[stop]) return null;

    const cityConfig = getConfigForCity(route.city);
    const disableStopID = cityConfig?.disableStopID || false;
    const stopHref = cityCode && route.city === 'all'
      ? `#/all/stops/${cityCode}^${stop}`
      : `#${route.cityPrefix}/stops/${stop}`;

    return (
      <a
        href={stopHref}
        data-stop={stop}
        onClick={(e) => {
          e.preventDefault();
          // Only drive navigation through the hash — it already triggers
          // the app's hashchange handler, which shows the stop popover.
          // Also calling onStopClick(stop) here used to fire that same
          // popover-setup logic a second time, synchronously, racing the
          // async hashchange-driven one (each with their own setState +
          // requestAnimationFrame + map.setFeatureState calls) — the
          // overlapping renders could wedge Preact's render scheduler,
          // freezing the popover until a full page reload.
          location.hash = stopHref;
          lastStop.current = stop;
        }}
      >
        {disableStopID ? (
          <>
            {stopsData[stop].name}
          </>
        ) : (
          <>
            <b class="mini-stop-tag">{stop}</b>
            <br />
            {stopsData[stop].name}
          </>
        )}
        {stopTimeLabels?.[stop] && <span class="stop-eta">{stopTimeLabels[stop]}</span>}
      </a>
    );
  };

  // Render vehicle indicator
  const VehicleIndicator = ({ vehicle }) => (
    <VehicleChip
      number={vehicle.vehicleNumber}
      load={vehicle.load}
      title={vehicle.serviceType ? `${vehicle.vehicleNumber} - ${vehicle.serviceType}` : vehicle.vehicleNumber}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onVehicleClick && vehicle.vehicleId) {
          onVehicleClick(vehicle.vehicleId);
        }
      }}
      style={{ cursor: onVehicleClick ? 'pointer' : 'default' }}
    />
  );

  // Render vehicles after a stop
  const VehiclesAfterStop = ({ stop }) => {
    // Ensure stop is a string for consistent lookup
    const stopKey = String(stop);
    const vehiclesHere = vehiclePositions.get(stopKey);
    if (!vehiclesHere || vehiclesHere.length === 0) return null;
    
    return (
      <div class="vehicles-at-stop">
        {vehiclesHere.map(vehicle => (
          <VehicleIndicator key={vehicle.vehicleId} vehicle={vehicle} />
        ))}
      </div>
    );
  };

  // Only 1 route & not a loop
  if (!route2 && route1FirstStop !== route1LastStop) {
    // Contains duplicates
    if (new Set(route1).size !== route1.length) {
      // Do nothing for now, this is very edge case
      console.info('Duplicate stops found on 1-way route.');
    }
    console.warn();
    return (
      <ol class="stops-list">
        {route1.map((s, index) => (
          <li key={`${s}-${index}`}>
            <StopLink stop={s} />
            <VehiclesAfterStop stop={s} />
          </li>
        ))}
      </ol>
    );
  } else {
    // Complex table layout for complex routes yo
    const stopGrid = [];

    // Create mutable copies
    let route1Copy, route2Copy;
    let loopRoute = false; // A->A
    if (route2) {
      route1Copy = route1.slice();
      route2Copy = route2.slice().reverse(); // Reverse for easier index reference
    } else {
      // Mostly likely a loop route A⟲B
      // So, split route1 into two routes
      loopRoute = true;
      let half;
      let hasMidStop = false;

      const hasDupStops = [];
      for (let i = 1, j = route1Len - 2; route1Len > 3 && !half; i++, j--) {
        const stop = route1[i];
        // console.log(i, j, route1, 1, stop);

        if (isOpposite(stop, oppositeStops)) {
          // WEIRD CASE: the route has duplicate stops!
          // Don't check first item because last item is its duplicate in loop route
          if (i !== route1.lastIndexOf(stop)) {
            // TODO: Handle this usecase?
            // Do nothing for now
            hasDupStops.push(stop);
          } else {
            // Normal case, let's find the middle index
            const opStop = getOpposite(route1[i], oppositeStops);
            if (opStop) {
              const index = route1.lastIndexOf(opStop);
              if (index > 0) j = index;
            }
          }
        }
        if (i === j - 1) {
          half = j;
        } else if (i === j) {
          half = j;
          hasMidStop = true;
        }
      }

      if (hasDupStops.length) {
        console.info('This loop route has duplicate stops.', hasDupStops);
      }

      route1Copy = route1.slice(0, half);
      route2Copy = route1
        .slice(-(route1Len - half - (hasMidStop ? 1 : 0)))
        .reverse();
      // console.log('x', half, route1, route1Copy, route2Copy);

      /*
      const half = Math.floor(route1Len / 2);
      route1Copy = route1.slice(0, half);
      route2Copy = route1.slice(-half).reverse();
      */

      if (hasMidStop) {
        // Odd
        const midStop = route1[half];
        if (areOpposite(midStop, route1[half - 1], stopsData)) {
          // ~~~ = Dummy stop to connect at the end
          route2Copy.push(midStop, '~~~');
          route1Copy.push('~~~');
        } else if (areOpposite(midStop, route1[half + 1], stopsData)) {
          route1Copy.push(midStop, '~~~');
          route2Copy.push('~~~');
        } else {
          // Throw in both because it'll be merged and rendered as a single stop later
          route1Copy.push(midStop);
          route2Copy.push(midStop);
        }
      } else {
        // Even
        route1Copy.push('~~~');
        route2Copy.push('~~~');
      }
    }
    // const oppositeStops = [];
    // route1Copy.forEach((s, i) => {
    //   const opS = getOpposite(s);
    //   if (opS) {
    //     if (
    //       route2Copy.includes(opS) &&
    //       !route1Copy.includes(opS) &&
    //       !route2Copy.includes(s)
    //     ) {
    //       oppositeStops.push(s, opS);
    //     }
    //   }
    // });

    let i = 0;
    const maxLoopCount = route1Copy.length + route2Copy.length;
    let col1IsEmpty = true,
      col2IsEmpty = true;
    let col1FirstStop = false,
      col2FirstStop = false,
      col1LastStop = false,
      col2LastStop = false;
    do {
      const stop1Opposite = getOpposite(route1Copy[0], oppositeStops);
      const stop2Opposite = getOpposite(route2Copy[0], oppositeStops);
      const stop1HasOpposite = stop1Opposite && route2Copy.includes(stop1Opposite);
      const stop2HasOpposite = stop2Opposite && route1Copy.includes(stop2Opposite);
      const stop1IsLast = route1Copy.length === 1 || !route1Copy.length;
      const stop2IsLast = route2Copy.length === 1 || !route2Copy.length;

      if (loopRoute && i === 0) {
        stopGrid[i] = [route1Copy.shift(), route2Copy.shift()];
      } else if (
        (stop1HasOpposite && !stop2HasOpposite) ||
        (stop1IsLast && !stop1HasOpposite && !stop2IsLast && !stop2HasOpposite)
      ) {
        stopGrid[i] = [null, route2Copy.shift()];
      } else if (
        (!stop1HasOpposite && stop2HasOpposite) ||
        (stop2IsLast && !stop2HasOpposite && !stop1IsLast && !stop1HasOpposite)
      ) {
        stopGrid[i] = [route1Copy.shift(), null];
      } else {
        stopGrid[i] = [route1Copy.shift(), route2Copy.shift()];
      }

      // Check empty columns
      if (stopGrid[i]?.[0] && col1IsEmpty) {
        col1IsEmpty = false;
      } else if (!stopGrid[i]?.[0] && !route1Copy.length) {
        col1IsEmpty = true;
      }
      if (stopGrid[i]?.[1] && col2IsEmpty) {
        col2IsEmpty = false;
      } else if (!stopGrid[i]?.[1] && !route2Copy.length) {
        col2IsEmpty = true;
      }

      // Extra metadata
      const metadata = {
        isSame: stopGrid[i][0] === stopGrid[i][1], // Same stop (very lazy check, no snapping)
        isOpposite: areOpposite(stopGrid[i][0], stopGrid[i][1], stopsData),
        col1IsEmpty,
        col2IsEmpty,
      };

      // Check first/last stops
      if (stopGrid[i]?.[0] && !col1FirstStop) {
        metadata.col1FirstStop = col1FirstStop = true;
      }
      if (stopGrid[i]?.[1] && !col2FirstStop) {
        metadata.col2FirstStop = col2FirstStop = true;
      }
      if (!route1Copy.length && !col1LastStop) {
        metadata.col1LastStop = col1LastStop = true;
      }
      if (!route2Copy.length && !col2LastStop) {
        metadata.col2LastStop = col2LastStop = true;
      }

      // Final metadata push
      stopGrid[i]?.push(metadata);

      // Infinite loop guard
      if (i > maxLoopCount + 2) {
        console.error(
          'Something is wrong. Might be infinite loop',
          i,
          route1Copy,
          route2Copy,
          stopGrid,
        );
        return null;
      }
      i++;
    } while (route1Copy[0] || route2Copy[0]);

    // Postfix for loop routes (A->A)
    if (loopRoute) {
      const lastRow = stopGrid[stopGrid.length - 1];
      const last2ndRow = stopGrid[stopGrid.length - 2];
      if (lastRow[0] === lastRow[1] && last2ndRow?.includes(null)) {
        const [s1, s2, meta] = last2ndRow;
        if (lastRow[0] === '~~~') {
          const theStop = /\d/.test(s1) ? s1 : s2;
          stopGrid[stopGrid.length - 2] = [
            theStop,
            theStop,
            {
              ...meta,
              col1LastStop: true,
              col2LastStop: true,
            },
          ];
          stopGrid.pop();
        } else if (lastRow[0]) {
          if (!s1) last2ndRow[0] = lastRow[0];
          if (!s2) last2ndRow[1] = lastRow[0];
          if (!s1 || !s2) {
            stopGrid[stopGrid.length - 1] = [
              '~~~',
              '~~~',
              {
                col1LastStop: true,
                col2LastStop: true,
              },
            ];
          }
        }
      }
    }

    const stopGridStops = [
      ...new Set(
        stopGrid
          .flat()
          .filter((value) => typeof value === 'string' && /\d/.test(value)),
      ),
    ];
    const routeStops = [...new Set([...route1, ...(route2 || [])])];
    if (stopGridStops.length !== routeStops.length) {
      console.error('Some stops are missing!', stopGridStops, routeStops);
    }

    return (
      <table class="stops-table">
          <colgroup>
            <col class="stop" />
            <col class="middle" />
            <col class="stop" />
          </colgroup>
          {stopGrid.map(
          (
            [
              s1,
              s2,
              {
                isSame,
                isOpposite,
                col1IsEmpty,
                col2IsEmpty,
                col1FirstStop,
                col2FirstStop,
                col1LastStop,
                col2LastStop,
              },
            ],
            index,
          ) => {
            const isEdgeRows =
              (index === 0 || index === stopGrid.length - 1) && s1 === s2;
            return (
              <Fragment key={`row-${index}`}>
                {/*
                  Safari needs this empty row
                  Else the table will break for A->B,C->D routes
                  Which means tables without this td[colspan=3]
                */}
                {index === 0 && (
                  <tr>
                    <td colspan="3"></td>
                  </tr>
                )}
                <tr class={isEdgeRows ? 'edge' : ''}>
                  {isEdgeRows ? (
                  <td
                    class={`stop-${
                      s1 === '~~~' ? 'u' : index === 0 ? 'start' : 'end'
                    } ${loopRoute ? 'loop' : ''}`}
                    colspan="3"
                  >
                    {s1 !== '~~~' && <StopLink stop={s1} />}
                    {s1 !== '~~~' && <VehiclesAfterStop stop={s1} />}
                  </td>
                  ) : (
                    <>
                      {s1 ? (
                        s1 !== '___' && (
                          <td
                            class={`${col1IsEmpty ? '' : 'stop'} ${
                              col1FirstStop ? 'first' : ''
                            } ${col1LastStop ? 'last' : ''}`}
                            rowspan={
                              isOpposite ||
                              index === stopGrid.length - 1 ||
                              index === 0
                                ? 1
                                : rowSpaner(stopGrid, 0, index)
                            }
                          >
                            <StopLink stop={s1} />
                            <VehiclesAfterStop stop={s1} />
                          </td>
                        )
                      ) : (
                        <td class={col1IsEmpty ? '' : 'empty'} />
                      )}
                      <td
                        class={isOpposite ? 'opposite' : isSame ? 'same' : ''}
                      />
                      {s2 ? (
                        s2 !== '___' && (
                          <td
                            class={`${col2IsEmpty ? '' : 'stop'} ${
                              col2FirstStop ? 'first' : ''
                            } ${col2LastStop ? 'last' : ''}`}
                            rowspan={
                              isOpposite ||
                              index === stopGrid.length - 1 ||
                              index === 0
                                ? 1
                                : rowSpaner(stopGrid, 1, index)
                            }
                          >
                            <StopLink stop={s2} />
                            <VehiclesAfterStop stop={s2} />
                          </td>
                        )
                      ) : (
                        <td class={col2IsEmpty ? '' : 'empty'} />
                      )}
                    </>
                  )}
                </tr>
              </Fragment>
            );
          },
        )}
      </table>
    );
  }
  return null;
}

export default memo(StopsList);
